import React, { useState } from 'react';

import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
  DeleteOutlined,
  LockOutlined,
  CalendarOutlined,
  SettingOutlined,
  AppstoreOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import { Can } from '@shared/permissions';
import { Alert, Descriptions, Space, Button, Tag, Empty, Table, Modal, Divider, Row, Col, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';
import { getPlatformCredentialFields, OAUTH_PLATFORMS, formatPlatformShippingCode } from '../../../constants/ecAccountPlatforms';
import { ModuleNameTag, ConnectionStatusTag, EnabledStatusTag } from './EcAccountTags';

// 抓單時間：value → label
const formatScheduleTime = (value) => {
  const hour = Math.floor(value / 100);
  const min = value % 100;
  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

const EcAccountShow = ({
  ecAccount,
  role = 'admin',
  onDelete,
  onScheduleFetch,
  onSyncProductMappings,
  onSyncStock,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scheduledFetch, setScheduledFetch] = useState(false);
  const [scheduleFetchLoading, setScheduleFetchLoading] = useState(false);
  const [syncMappingsLoading, setSyncMappingsLoading] = useState(false);
  const [syncStockLoading, setSyncStockLoading] = useState(false);

  const handleEdit = () => {
    navigate(`/ec_accounts/${ecAccount.id}/edit`);
  };

  const handleScheduleFetch = async () => {
    if (!onScheduleFetch) return;
    setScheduleFetchLoading(true);
    try {
      await onScheduleFetch(ecAccount.id);
      setScheduledFetch(true);
    } finally {
      setScheduleFetchLoading(false);
    }
  };

  const handleSyncProductMappings = async () => {
    if (!onSyncProductMappings) return;
    setSyncMappingsLoading(true);
    try {
      await onSyncProductMappings(ecAccount.id);
    } finally {
      setSyncMappingsLoading(false);
    }
  };

  const handleSyncStock = () => {
    if (!onSyncStock) return;
    Modal.confirm({
      title: t('models.ec_account.sync_stock_confirm_title'),
      content: t('models.ec_account.sync_stock_confirm_content'),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setSyncStockLoading(true);
        try {
          await onSyncStock(ecAccount.id);
        } finally {
          setSyncStockLoading(false);
        }
      },
    });
  };

  if (!ecAccount) {
    return <Empty description={t('common.error.no_data')} />;
  }

  const shippingTypeMappings = ecAccount.shipping_type_mappings || [];
  const basePath = role === 'operator' ? '/operator' : '/admin';
  const displayName = ecAccount.display_name || ecAccount.module_name;
  const lastItem = { label: displayName };
  const breadcrumbItems = role === 'operator'
    ? ['ec_accounts', lastItem]
    : ['platform', 'platform.ec_accounts', lastItem];

  const titleNode = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <ModuleNameTag moduleName={ecAccount.module_name} style={{ margin: 0, fontSize: 13 }} />
      <span>{displayName}</span>
    </span>
  );

  const mappingsForDisplay = React.useMemo(() => {
    return shippingTypeMappings.map((m) => ({
      key: m.id,
      code: m.ec_platform_shipping_code === '' ? '' : (m.ec_platform_shipping_code || '---'),
      shipping_type: m.shipping_type_name || m.shipping_type_id || '-',
    }));
  }, [shippingTypeMappings]);

  const canDelete = !!onDelete;
  const isConnected = ecAccount.connection_status === 'connected';
  const canShowScheduleFetch = onScheduleFetch && isConnected && ecAccount.enable_fetch_orders;
  // Single source of truth: backend EcAccount#sync_product_mappings_supported? / #manual_stock_sync_supported?
  // 均已包含 isConnected + stock_push_enabled + 平台白名單三項判斷
  const canShowSyncMappings = onSyncProductMappings && ecAccount.sync_product_mappings_supported;
  const canShowSyncStock = onSyncStock && ecAccount.manual_stock_sync_supported;

  // 頂部僅留帳號級別操作（編輯、刪除）；同步類動作下放到對應功能區塊（in-place）
  const headerActions = (
    <Space size="small">
      <Can requiredPermission="ec_accounts:write">
        <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
          {t('common.button.edit')}
        </Button>
      </Can>
      {canDelete && (
        <>
          <Divider type="vertical" style={{ height: 24, margin: '0 4px' }} />
          <Can requiredPermission="ec_accounts:delete">
            <Tooltip title={t('common.button.delete')}>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: t('common.message.confirm_delete'),
                    okText: t('common.button.confirm'),
                    cancelText: t('common.button.cancel'),
                    okButtonProps: { danger: true },
                    onOk: onDelete,
                  });
                }}
              />
            </Tooltip>
          </Can>
        </>
      )}
    </Space>
  );

  const scheduleFetchAction = canShowScheduleFetch && (
    scheduledFetch ? (
      <Tag color="success" icon={<CheckOutlined />} style={{ margin: 0 }}>
        {t('models.ec_account.schedule_fetch_scheduled', { defaultValue: '已排程' })}
      </Tag>
    ) : (
      <Can requiredPermission="ec_accounts:write">
        <Button
          size="small"
          icon={<CalendarOutlined />}
          loading={scheduleFetchLoading}
          onClick={handleScheduleFetch}
        >
          {t('models.ec_account.schedule_fetch', { defaultValue: '排程抓取訂單' })}
        </Button>
      </Can>
    )
  );

  const syncMappingsAction = canShowSyncMappings && (
    <Can requiredPermission="ec_accounts:write">
      <Button
        size="small"
        icon={<AppstoreOutlined />}
        loading={syncMappingsLoading}
        onClick={handleSyncProductMappings}
      >
        {t('models.ec_account.sync_product_mappings')}
      </Button>
    </Can>
  );

  const syncStockAction = canShowSyncStock && (
    <Can requiredPermission="ec_accounts:write">
      <Button
        size="small"
        type="primary"
        icon={<SyncOutlined />}
        loading={syncStockLoading}
        onClick={handleSyncStock}
      >
        {t('models.ec_account.sync_stock')}
      </Button>
    </Can>
  );

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={breadcrumbItems}
        title={titleNode}
        actions={headerActions}
      />

      {ecAccount.connection_status === 'disconnected' && (() => {
        const isOAuth = OAUTH_PLATFORMS.includes(ecAccount.module_name);
        return (
          <Alert
            message={isOAuth
              ? t('models.ec_account.disconnected_alert_oauth')
              : t('models.ec_account.disconnected_alert')}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={isOAuth
              ? (
                <Can requiredPermission="ec_accounts:write">
                  <Button size="small" type="primary" danger href={`/third_parties/${ecAccount.module_name.toLowerCase()}/auth?ec_account_id=${ecAccount.id}&source=${role}&mode=show`}>{t('models.ec_account.reauthorize')}</Button>
                </Can>
              )
              : (
                <Can requiredPermission="ec_accounts:write">
                  <Button size="small" onClick={handleEdit}>{t('common.button.edit')}</Button>
                </Can>
              )}
          />
        );
      })()}

      <Row gutter={24}>
        {/* 左半邊：連線資訊 */}
        <Col span={10}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {t('models.ec_account.connection_panel_title')}
            </span>
            <ConnectionStatusTag status={ecAccount.connection_status} />
          </div>
          <Divider style={{ marginTop: 0 }} />

          {/* 基本資訊 */}
          <Descriptions column={1} size="small" bordered>
            {role === 'operator' && (
              <Descriptions.Item label={t('common.filter.merchant')}>
                {ecAccount.merchant_name || '-'}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('models.ec_account.module_name')}>
              {ecAccount.module_name ? <ModuleNameTag moduleName={ecAccount.module_name} /> : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.ec_account.display_name')}>
              {ecAccount.display_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.sales_channel.name')}>
              {ecAccount.sales_channel_name || '-'}
            </Descriptions.Item>
          </Descriptions>

          {/* 串接資訊 */}
          <Divider orientation="left"><Space><LockOutlined />{t('models.ec_account.credentials', { defaultValue: '串接資訊' })}</Space></Divider>
          <Descriptions column={1} size="small" bordered>
            {getPlatformCredentialFields(ecAccount.module_name).length > 0 ? (
              getPlatformCredentialFields(ecAccount.module_name).map(({ key, labelKey }) => (
                <Descriptions.Item key={key} label={t(labelKey)}>
                  {String(ecAccount.credentials?.[key] ?? '')}
                </Descriptions.Item>
              ))
            ) : (
              <Descriptions.Item>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.error.no_data')} />
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* 時間戳記 */}
          <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
            <span>{t('common.created_at')}: {ecAccount.created_at ? formatDateTime(ecAccount.created_at) : '-'}</span>
            <span style={{ marginLeft: 16 }}>{t('common.updated_at')}: {ecAccount.updated_at ? formatDateTime(ecAccount.updated_at) : '-'}</span>
          </div>
        </Col>

        {/* 右半邊：功能設定 */}
        <Col span={14}>
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {t('models.ec_account.settings_panel_title')}
            </span>
          </div>
          <Divider style={{ marginTop: 0 }} />

          {/* 抓單設定 */}
          <div style={{ marginBottom: 20, padding: 16, background: '#fafafa', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Space size={8}>
                <span style={{ fontWeight: 500 }}>{t('models.ec_account.enable_fetch_orders')}</span>
                <EnabledStatusTag value={!!ecAccount.enable_fetch_orders} style={{ margin: 0 }} />
              </Space>
              {scheduleFetchAction}
            </div>
            {ecAccount.enable_fetch_orders && (
              <>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>{t('models.ec_account.schedule_times', { defaultValue: '抓單時間' })}</div>
                <Space wrap>
                  {(ecAccount.fetch_schedule || []).map((time) => (
                    <Tag key={time}>{formatScheduleTime(time)}</Tag>
                  ))}
                </Space>
                {ecAccount.fetch_orders_next_run_at && (
                  <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                    {t('models.ec_account.fetch_orders_next_run_at', { defaultValue: '下次抓單時間' })}: {formatDateTime(ecAccount.fetch_orders_next_run_at)}
                  </div>
                )}
              </>
            )}
          </div>

          {/* 入庫推送設定 */}
          <div style={{ marginBottom: 20, padding: 16, background: '#fafafa', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Space size={8}>
                <span style={{ fontWeight: 500 }}>{t('models.ec_account.stock_push_enabled')}</span>
                <EnabledStatusTag value={!!ecAccount.stock_push_enabled} style={{ margin: 0 }} />
              </Space>
              <Space size="small">
                {syncMappingsAction}
                {syncStockAction}
              </Space>
            </div>
            {ecAccount.stock_push_enabled && (
              <>
                <div style={{ fontSize: 14, color: '#333', marginBottom: 8 }}>
                  {t('models.ec_account.stock_percentage')}: <span style={{ fontWeight: 500 }}>{ecAccount.stock_percentage != null ? `${ecAccount.stock_percentage}%` : '-'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#999' }}>
                  <AppstoreOutlined style={{ marginRight: 4 }} />
                  {ecAccount.product_ec_mappings_count > 0
                    ? t('models.ec_account.product_mappings_count', {
                        productCount: ecAccount.product_ec_mappings_product_count,
                        mappingCount: ecAccount.product_ec_mappings_count,
                      })
                    : t('models.ec_account.product_mappings_empty')}
                </div>
                {ecAccount.product_mapping_synced_at && (
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    {t('models.ec_account.product_mapping_synced_at')}: {formatDateTime(ecAccount.product_mapping_synced_at)}
                  </div>
                )}
              </>
            )}
          </div>

          {/* 物流代碼對應 */}
          {mappingsForDisplay.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Divider orientation="left"><Space><LinkOutlined />{t('models.ec_account.shipping_mapping_settings', { defaultValue: '物流代碼對應' })}</Space></Divider>
              <Table
                dataSource={mappingsForDisplay}
                rowKey="key"
                pagination={false}
                size="small"
                bordered
                columns={[
                  {
                    title: t('models.ec_account.ec_platform_shipping_code'),
                    dataIndex: 'code',
                    key: 'code',
                    width: '40%',
                    render: (code) => code === '' ? <Tag color="orange">廠商配送</Tag> : formatPlatformShippingCode(code, ecAccount.module_name),
                  },
                  {
                    title: t('models.ec_account.warehouse_shipping_type', { defaultValue: '倉庫端物流' }),
                    dataIndex: 'shipping_type',
                    key: 'shipping_type',
                    width: '60%',
                  },
                ]}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default EcAccountShow;
